import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// Consultant Dashboard API Service
export const consultantDashboardApi = createApi({
  reducerPath: "consultantDashboardApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_BACKEND_URL}`,
    credentials: "include",
  }),
  tagTypes: ["ConsultantAlerts", "ConsultantCharts", "ConsultantRequests"],
  endpoints: (builder) => ({
    // Get consultant alerts summary (widgets)
    getConsultantAlertsSummary: builder.query({
      query: () => "/ir/consultant/dashboard",
      providesTags: ["ConsultantAlerts"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message:
          response?.data?.message ||
          "Failed to fetch consultant alerts summary",
      }),
    }),

    // Get charts data (bar and pie chart)
    getConsultantCharts: builder.query({
      query: () => "/consultant/dashboard/charts",
      providesTags: ["ConsultantCharts"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message:
          response?.data?.message || "Failed to fetch consultant charts data",
      }),
    }),

    // Get latest requests
    getLatestRequests: builder.query({
      query: ({ limit = 10 }) =>
        `/consultant/dashboard/latest-requests?limit=${limit}`,
      providesTags: ["ConsultantRequests"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response?.data?.message || "Failed to fetch latest requests",
      }),
    }),
    // Get QA alerts summary (widgets)
    getQAAlertsSummary: builder.query({
      query: (params = {}) => {
        const { fromDate, toDate, type, projectId } = params;
        let queryString = "";
        const queryParams = [];

        if (fromDate) queryParams.push(`fromDate=${fromDate}`);
        if (toDate) queryParams.push(`toDate=${toDate}`);
        if (type && type !== "all") queryParams.push(`type=${type}`);
        if (projectId && projectId !== "all")
          queryParams.push(`projectId=${projectId}`);

        if (queryParams.length > 0) {
          queryString = "?" + queryParams.join("&");
        }

        return {
          url: `https://fom.softsprint.ae/backendTest/qaqcDashboard/overview${queryString}`,
        };
      },
      providesTags: ["QAAlerts"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response?.data?.message || "Failed to fetch QA alerts summary",
      }),
    }),

    // Get requests by module
    getQARequestsByModule: builder.query({
      query: (params = {}) => {
        const { fromDate, toDate, type, projectId } = params;
        let queryString = "";
        const queryParams = [];

        if (fromDate) queryParams.push(`fromDate=${fromDate}`);
        if (toDate) queryParams.push(`toDate=${toDate}`);
        if (type && type !== "all") queryParams.push(`type=${type}`);
        if (projectId && projectId !== "all")
          queryParams.push(`projectId=${projectId}`);

        if (queryParams.length > 0) {
          queryString = "?" + queryParams.join("&");
        }

        return {
          url: `https://fom.softsprint.ae/backendTest/qaqcDashboard/requests/by-module${queryString}`,
        };
      },
      providesTags: ["QACharts"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message:
          response?.data?.message || "Failed to fetch requests by module",
      }),
    }),

    // Get pending by team
    getQAPendingByTeam: builder.query({
      query: (params = {}) => {
        const { fromDate, toDate, type, projectId } = params;
        let queryString = "";
        const queryParams = [];

        if (fromDate) queryParams.push(`fromDate=${fromDate}`);
        if (toDate) queryParams.push(`toDate=${toDate}`);
        if (type && type !== "all") queryParams.push(`type=${type}`);
        if (projectId && projectId !== "all")
          queryParams.push(`projectId=${projectId}`);

        if (queryParams.length > 0) {
          queryString = "?" + queryParams.join("&");
        }

        return {
          url: `https://fom.softsprint.ae/backendTest/qaqcDashboard/pending/by-team${queryString}`,
        };
      },
      providesTags: ["QACharts"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message: response?.data?.message || "Failed to fetch pending by team",
      }),
    }),

    // Get contractors performance
    getQAContractorsPerformance: builder.query({
      query: (params = {}) => {
        const { fromDate, toDate, type, projectId } = params;
        let queryString = "";
        const queryParams = [];

        if (fromDate) queryParams.push(`fromDate=${fromDate}`);
        if (toDate) queryParams.push(`toDate=${toDate}`);
        if (type && type !== "all") queryParams.push(`type=${type}`);
        if (projectId && projectId !== "all")
          queryParams.push(`projectId=${projectId}`);

        if (queryParams.length > 0) {
          queryString = "?" + queryParams.join("&");
        }

        return {
          url: `https://fom.softsprint.ae/backendTest/qaqcDashboard/contractors/performance${queryString}`,
        };
      },
      providesTags: ["QACharts"],
      transformErrorResponse: (response) => ({
        status: response.status,
        message:
          response?.data?.message || "Failed to fetch contractors performance",
      }),
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetConsultantAlertsSummaryQuery,
  useGetConsultantChartsQuery,
  useGetLatestRequestsQuery,
  useGetQAAlertsSummaryQuery,
  useGetQARequestsByModuleQuery,
  useGetQAPendingByTeamQuery,
  useGetQAContractorsPerformanceQuery,
} = consultantDashboardApi;

// Export the reducer and middleware
export default consultantDashboardApi;
